import React from 'react';
import { Info, Landmark, TrendingUp, Percent, Calendar, FileText, CreditCard, AlertTriangle } from 'lucide-react';

/**
 * Компонент с информацией о дивидендах и советами
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-green-500" />
        Всё о дивидендах: понятие, налоги, стратегии
      </h2>
      
      <div className="prose prose-green dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Landmark className="w-5 h-5 text-green-500" />
          Что такое дивиденды?
        </h3>
        <p>
          <strong>Дивиденды</strong> — это часть прибыли компании, которая распределяется между акционерами пропорционально количеству принадлежащих им акций. Дивиденды являются одним из способов получения дохода от инвестиций в акции, наряду с возможностью прироста капитала за счет увеличения рыночной стоимости акций.
        </p>
        <p>
          Компании могут выплачивать дивиденды с разной периодичностью: ежегодно, раз в полгода, ежеквартально или ежемесячно. В России наиболее распространены ежегодные и полугодовые выплаты. Размер дивидендов может быть фиксированным (типично для привилегированных акций) или меняться в зависимости от финансовых результатов компании.
        </p>

        <h3 className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-green-500" />
          Ключевые даты при выплате дивидендов
        </h3>
        <ul>
          <li><strong>Дата объявления (announcement date)</strong> — день, когда компания официально объявляет о выплате дивидендов</li>
          <li><strong>Дата закрытия реестра (record date)</strong> — дата, на которую определяется список акционеров, имеющих право на получение дивидендов</li>
          <li><strong>Экс-дивидендная дата (ex-dividend date)</strong> — дата, начиная с которой акции продаются уже без права на объявленные дивиденды. В России это обычно 2 рабочих дня до даты закрытия реестра</li>
          <li><strong>Дата выплаты (payment date)</strong> — день, когда происходит фактическая выплата дивидендов</li>
        </ul>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium">Дивидендная доходность (Dividend Yield)</h4>
          <p className="text-blue-600 dark:text-blue-400 text-sm mb-0">
            Дивидендная доходность — это отношение годовых дивидендов на акцию к текущей цене акции, выраженное в процентах. 
            Формула: (Годовой дивиденд на акцию / Текущая цена акции) × 100%. 
            Например, если акция стоит 1000 рублей и выплачивает 50 рублей дивидендов в год, дивидендная доходность составляет 5%.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <Percent className="w-5 h-5 text-green-500" />
          Налогообложение дивидендов в России
        </h3>
        <p>
          В России дивиденды облагаются налогом на доходы физических лиц (НДФЛ). С 2021 года ставка налога составляет:
        </p>
        <ul>
          <li><strong>13%</strong> — для налоговых резидентов РФ</li>
          <li><strong>15%</strong> — для нерезидентов РФ</li>
        </ul>
        <p>
          Особенности налогообложения дивидендов от российских компаний:
        </p>
        <ul>
          <li>Налог с дивидендов от российских компаний удерживается у источника выплаты (брокер или эмитент)</li>
          <li>Налог автоматически перечисляется в бюджет до поступления денег на счет инвестора</li>
          <li>Самостоятельно подавать налоговую декларацию не требуется</li>
        </ul>
        <p>
          При получении дивидендов от иностранных компаний:
        </p>
        <ul>
          <li>Зарубежный налог обычно удерживается у источника выплаты по ставке страны эмитента</li>
          <li>В России необходимо самостоятельно подать декларацию и доплатить разницу между уже уплаченным налогом и российской ставкой</li>
          <li>При наличии соглашения об избежании двойного налогообложения (СИДН) часть уплаченного за рубежом налога может быть зачтена</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-green-500" />
          Дивидендные стратегии инвестирования
        </h3>
        <p>
          Существует несколько популярных стратегий инвестирования, ориентированных на дивиденды:
        </p>
        <ul>
          <li>
            <strong>Стратегия высоких дивидендных выплат</strong> — инвестирование в акции с высокой текущей дивидендной доходностью для получения регулярного дохода
          </li>
          <li>
            <strong>Стратегия роста дивидендов</strong> — инвестирование в компании, которые стабильно увеличивают размер дивидендов каждый год
          </li>
          <li>
            <strong>Дивидендные аристократы</strong> — компании, которые повышают размер дивидендов не менее 25 лет подряд
          </li>
          <li>
            <strong>Стратегия реинвестирования дивидендов</strong> — использование полученных дивидендов для покупки дополнительных акций, что позволяет задействовать силу сложного процента
          </li>
        </ul>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            На что обратить внимание при выборе дивидендных акций
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li><strong>Коэффициент выплаты дивидендов (payout ratio)</strong> — отношение выплачиваемых дивидендов к чистой прибыли компании. Слишком высокий коэффициент может быть признаком того, что компания выплачивает больше, чем может себе позволить</li>
            <li><strong>История выплат</strong> — компании с длительной историей стабильных или растущих дивидендов обычно надежнее</li>
            <li><strong>Финансовое состояние компании</strong> — уровень долга, денежные потоки, рентабельность и перспективы роста</li>
            <li><strong>Дивидендная политика</strong> — четко сформулированная дивидендная политика указывает на приверженность компании к выплатам акционерам</li>
            <li><strong>Отраслевая специфика</strong> — некоторые отрасли традиционно выплачивают более высокие дивиденды (коммунальные услуги, телекоммуникации, REIT)</li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-green-500" />
          Дивидендная политика российских компаний
        </h3>
        <p>
          В последние годы многие российские компании пересмотрели свою дивидендную политику в сторону увеличения выплат. Часто встречаются следующие подходы:
        </p>
        <ul>
          <li>Выплата фиксированного процента от чистой прибыли по МСФО (часто 50% и выше)</li>
          <li>Выплата с привязкой к свободному денежному потоку</li>
          <li>Обязательство по минимальному размеру дивидендов</li>
          <li>Выплаты несколько раз в год (промежуточные дивиденды)</li>
        </ul>
        <p>
          Крупнейшие дивидендные компании России традиционно сосредоточены в сырьевых секторах:
        </p>
        <ul>
          <li>Нефтегазовый сектор (Газпром, Лукойл, Роснефть, Сургутнефтегаз, Новатэк)</li>
          <li>Металлургия и горнодобыча (Норильский Никель, Северсталь, НЛМК, Алроса)</li>
          <li>Также значительные дивиденды выплачивают телекоммуникационные компании (МТС), некоторые банки (Сбербанк, ВТБ) и отдельные представители других секторов</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-green-500" />
          Как начать инвестировать в дивидендные акции
        </h3>
        <ol>
          <li><strong>Откройте брокерский счет</strong> — выберите надежного брокера с удобным сервисом и оптимальными тарифами</li>
          <li><strong>Определите инвестиционные цели</strong> — текущий доход или долгосрочный рост капитала</li>
          <li><strong>Изучите дивидендные акции</strong> — исследуйте компании с точки зрения дивидендной истории, финансовых показателей и перспектив</li>
          <li><strong>Формируйте диверсифицированный портфель</strong> — распределите инвестиции между разными секторами и компаниями для снижения рисков</li>
          <li><strong>Рассмотрите дивидендные фонды</strong> — ETF и ПИФы с фокусом на дивидендные акции могут быть хорошим решением для начинающих инвесторов</li>
          <li><strong>Регулярно пересматривайте портфель</strong> — контролируйте изменения в дивидендной политике компаний и их финансовых показателях</li>
        </ol>

        <p className="font-semibold mt-6">
          Наш калькулятор дивидендов поможет вам спланировать потенциальный доход от дивидендных инвестиций с учетом налогов, роста дивидендов и реинвестирования!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;